// Settings Modal - Kiosk Configuration
import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { clsx } from 'clsx';
import { useSettingsStore, type SearchBarPosition, type VoiceMode } from '@/store/settingsStore';
import { useAuthStore } from '@/store/authStore';

export function SettingsButton() {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <>
      <button
        onClick={() => setIsOpen(true)}
        className={clsx(
          'w-10 h-10 rounded-full flex items-center justify-center',
          'bg-workshop-surface border border-workshop-border',
          'text-workshop-text-muted hover:text-white hover:border-workshop-accent',
          'transition-all duration-200 cursor-pointer hover:scale-105'
        )}
        title="Kiosk Settings"
      >
        <GearIcon className="w-5 h-5" />
      </button>

      <AnimatePresence>
        {isOpen && <SettingsModal onClose={() => setIsOpen(false)} />}
      </AnimatePresence>
    </>
  );
}

function SettingsModal({ onClose }: { onClose: () => void }) {
  const {
    searchBarPosition,
    setSearchBarPosition,
    voiceMode,
    setVoiceMode,
    voiceFollowUpDuration,
    setVoiceFollowUpDuration,
    offlineVoice,
    setOfflineVoice,
  } = useSettingsStore();

  const isKioskMode = import.meta.env.VITE_REQUIRE_PAIRING === 'true';
  const clearAuth = useAuthStore((s) => s.clearAuth);

  const handleLogOut = () => {
    if (isKioskMode) {
      clearAuth();
      window.location.reload();
    } else {
      // Access Clerk instance from window (set by ClerkProvider)
      (window as any).Clerk?.signOut();
    }
  };

  return (
    <>
      {/* Backdrop */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
        className="fixed inset-0 z-[100] bg-black/60"
        onClick={onClose}
      />

      {/* Modal */}
      <motion.div
        initial={{ opacity: 0, y: -20, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: -20, scale: 0.95 }}
        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
        drag
        dragMomentum={false}
        dragElastic={0.1}
        className="fixed z-[101] top-1/2 left-1/2 w-[90vw] max-w-md overflow-hidden bg-workshop-bg border border-workshop-border rounded-2xl shadow-2xl cursor-default"
        style={{ x: '-50%', y: '-50%' }}
      >
        {/* Drag Handle */}
        <div className="flex justify-center py-2 cursor-grab active:cursor-grabbing bg-workshop-surface border-b border-workshop-border/50">
          <div className="w-12 h-1.5 rounded-full bg-workshop-border hover:bg-workshop-text-muted transition-colors" />
        </div>

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-workshop-border bg-workshop-surface">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-workshop-accent/20 flex items-center justify-center">
              <GearIcon className="w-4 h-4 text-workshop-accent" />
            </div>
            <h2 className="text-lg font-semibold text-white">Kiosk Settings</h2>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full flex items-center justify-center text-workshop-text-muted hover:text-white hover:bg-workshop-border transition-all duration-150"
          >
            <CloseIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="px-5 py-5 space-y-6">
          {/* Search Bar Position */}
          <div>
            <label className="text-sm font-medium text-workshop-text-muted mb-3 block">
              Search Bar Location
            </label>
            <p className="text-xs text-workshop-text-muted/70 mb-4">
              Choose based on kiosk mounting height
            </p>
            <div className="grid grid-cols-2 gap-3">
              <PositionOption
                position="top"
                label="Top"
                description="Standard desktop"
                selected={searchBarPosition === 'top'}
                onSelect={() => setSearchBarPosition('top')}
              />
              <PositionOption
                position="bottom"
                label="Bottom"
                description="High-mounted display"
                selected={searchBarPosition === 'bottom'}
                onSelect={() => setSearchBarPosition('bottom')}
              />
            </div>
          </div>

          {/* Voice Mode */}
          <div>
            <label className="text-sm font-medium text-workshop-text-muted mb-3 block">
              Voice Mode
            </label>
            <p className="text-xs text-workshop-text-muted/70 mb-4">
              How Ventura listens for voice commands
            </p>
            <div className="grid grid-cols-2 gap-3">
              <VoiceModeOption
                mode="full"
                label="Full Voice"
                description="Always listening for 'Hey Ventura'"
                selected={voiceMode === 'full'}
                onSelect={() => setVoiceMode('full')}
              />
              <VoiceModeOption
                mode="ptt"
                label="Push-to-Talk"
                description="Click button or press V"
                selected={voiceMode === 'ptt'}
                onSelect={() => setVoiceMode('ptt')}
              />
            </div>
          </div>

          {/* Offline Voice Toggle */}
          <div>
            <label className="text-sm font-medium text-workshop-text-muted mb-3 block">
              Offline Voice Recognition
            </label>
            <p className="text-xs text-workshop-text-muted/70 mb-4">
              Use Sherpa-ONNX for fully offline voice (no internet required)
            </p>
            <button
              onClick={() => setOfflineVoice(!offlineVoice)}
              className={clsx(
                'flex items-center gap-3 w-full p-4 rounded-xl border-2 transition-all duration-200',
                offlineVoice
                  ? 'border-workshop-accent bg-workshop-accent/10'
                  : 'border-workshop-border hover:border-workshop-text-muted bg-workshop-surface/50'
              )}
            >
              <div className={clsx(
                'w-12 h-7 rounded-full relative transition-colors duration-200',
                offlineVoice ? 'bg-workshop-accent' : 'bg-workshop-border'
              )}>
                <div className={clsx(
                  'absolute top-1 w-5 h-5 rounded-full bg-white transition-all duration-200',
                  offlineVoice ? 'left-6' : 'left-1'
                )} />
              </div>
              <div className="flex-1 text-left">
                <p className={clsx(
                  'font-medium text-sm',
                  offlineVoice ? 'text-white' : 'text-workshop-text-muted'
                )}>
                  {offlineVoice ? 'Offline Mode' : 'Online Mode'}
                </p>
                <p className="text-xs text-workshop-text-muted/70">
                  {offlineVoice ? 'Using local WASM (works without internet)' : 'Using Web Speech API (requires internet)'}
                </p>
              </div>
              {offlineVoice && (
                <WifiOffIcon className="w-5 h-5 text-workshop-accent" />
              )}
            </button>
          </div>

          {/* Voice Follow-up Duration - Only show in Full Voice mode */}
          {voiceMode === 'full' && (
            <div>
              <label className="text-sm font-medium text-workshop-text-muted mb-3 block">
                Voice Follow-up Window
              </label>
              <p className="text-xs text-workshop-text-muted/70 mb-4">
                How long to stay in command mode after wake word ({voiceFollowUpDuration}s)
              </p>
              <div className="flex items-center gap-4">
                <span className="text-xs text-workshop-text-muted w-8">5s</span>
                <input
                  type="range"
                  min="5"
                  max="30"
                  step="1"
                  value={voiceFollowUpDuration}
                  onChange={(e) => setVoiceFollowUpDuration(Number(e.target.value))}
                  className="flex-1 h-2 bg-workshop-border rounded-lg appearance-none cursor-pointer accent-workshop-accent"
                />
                <span className="text-xs text-workshop-text-muted w-8">30s</span>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t border-workshop-border bg-workshop-surface/50 space-y-3">
          {/* Logout Button */}
          <button
            onClick={() => {
              handleLogOut();
              onClose();
            }}
            className="w-full px-4 py-2.5 rounded-lg border border-red-500/30 bg-red-500/10 text-red-400 hover:bg-red-500/20 hover:border-red-500/50 transition-all duration-200 text-sm font-medium"
          >
            {isKioskMode ? 'Unpair Device' : 'Log Out'}
          </button>

          <p className="text-workshop-text-muted text-xs text-center">
            Settings are saved automatically
          </p>
        </div>
      </motion.div>
    </>
  );
}

function PositionOption({
  position,
  label,
  description,
  selected,
  onSelect,
}: {
  position: SearchBarPosition;
  label: string;
  description: string;
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      onClick={onSelect}
      className={clsx(
        'relative p-4 rounded-xl border-2 transition-all duration-200 text-left',
        selected
          ? 'border-workshop-accent bg-workshop-accent/10'
          : 'border-workshop-border hover:border-workshop-text-muted bg-workshop-surface/50'
      )}
    >
      {/* Visual preview */}
      <div className="w-full h-16 mb-3 rounded-lg bg-workshop-bg border border-workshop-border/50 relative overflow-hidden">
        <div
          className={clsx(
            'absolute left-1 right-1 h-2 rounded-sm',
            selected ? 'bg-workshop-accent' : 'bg-workshop-text-muted/30',
            position === 'top' ? 'top-1' : 'bottom-1'
          )}
        />
        {/* Content area dots */}
        <div className={clsx(
          'absolute left-2 right-2 flex gap-1',
          position === 'top' ? 'top-5' : 'top-2'
        )}>
          <div className="w-2 h-2 rounded-sm bg-workshop-border/50" />
          <div className="w-2 h-2 rounded-sm bg-workshop-border/50" />
          <div className="w-2 h-2 rounded-sm bg-workshop-border/50" />
        </div>
      </div>

      <p className={clsx(
        'font-medium text-sm',
        selected ? 'text-white' : 'text-workshop-text-muted'
      )}>
        {label}
      </p>
      <p className="text-xs text-workshop-text-muted/70 mt-0.5">
        {description}
      </p>

      {/* Selected indicator */}
      {selected && (
        <div className="absolute top-2 right-2 w-5 h-5 rounded-full bg-workshop-accent flex items-center justify-center">
          <CheckIcon className="w-3 h-3 text-white" />
        </div>
      )}
    </button>
  );
}

function VoiceModeOption({
  mode,
  label,
  description,
  selected,
  onSelect,
}: {
  mode: VoiceMode;
  label: string;
  description: string;
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      onClick={onSelect}
      className={clsx(
        'relative p-4 rounded-xl border-2 transition-all duration-200 text-left',
        selected
          ? 'border-workshop-accent bg-workshop-accent/10'
          : 'border-workshop-border hover:border-workshop-text-muted bg-workshop-surface/50'
      )}
    >
      {/* Visual preview */}
      <div className="w-full h-16 mb-3 rounded-lg bg-workshop-bg border border-workshop-border/50 relative overflow-hidden flex items-center justify-center">
        {mode === 'full' ? (
          // Waveform icon for always-listening
          <div className="flex items-center gap-0.5">
            {[3, 5, 8, 6, 4, 7, 5, 3].map((h, i) => (
              <div
                key={i}
                className={clsx(
                  'w-1 rounded-full',
                  selected ? 'bg-workshop-accent' : 'bg-workshop-text-muted/40'
                )}
                style={{ height: `${h * 3}px` }}
              />
            ))}
          </div>
        ) : (
          // Button icon for push-to-talk
          <div className={clsx(
            'w-10 h-10 rounded-full border-2 flex items-center justify-center',
            selected ? 'border-workshop-accent bg-workshop-accent/20' : 'border-workshop-text-muted/40 bg-workshop-surface'
          )}>
            <MicIcon className={clsx(
              'w-5 h-5',
              selected ? 'text-workshop-accent' : 'text-workshop-text-muted/60'
            )} />
          </div>
        )}
      </div>

      <p className={clsx(
        'font-medium text-sm',
        selected ? 'text-white' : 'text-workshop-text-muted'
      )}>
        {label}
      </p>
      <p className="text-xs text-workshop-text-muted/70 mt-0.5">
        {description}
      </p>

      {/* Selected indicator */}
      {selected && (
        <div className="absolute top-2 right-2 w-5 h-5 rounded-full bg-workshop-accent flex items-center justify-center">
          <CheckIcon className="w-3 h-3 text-white" />
        </div>
      )}
    </button>
  );
}

function MicIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
    </svg>
  );
}

function GearIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  );
}

function CloseIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
    </svg>
  );
}

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
    </svg>
  );
}

function WifiOffIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M18.364 5.636a9 9 0 010 12.728m0 0l-2.829-2.829m2.829 2.829L21 21M15.536 8.464a5 5 0 010 7.072m0 0l-2.829-2.829m-4.243 2.829a4.978 4.978 0 01-1.414-2.83m-1.414 5.658a9 9 0 01-2.167-9.238m7.824 2.167a1 1 0 111.414 1.414m-1.414-1.414L3 3m8.293 8.293l1.414 1.414" />
    </svg>
  );
}
